<template>
  <div class="h-full flex flex-col">
    <div
      v-if="showTabs"
      class="flex-shrink h-10 w-full flex items-center flex-row dark:bg-gray-900 dark:text-white"
      :class="navClass"
    >
      <slot name="leading" />

      <!-- Left nav arrow-->
      <template v-if="showArrows">
        <o-button
          flat
          size="sm"
          icon="chevronLeft"
          @click="handleMoveScroll(-1)"
        />
        <div class="py-2 pl-2 h-full">
          <span class="border-r dark:border-gray-800 h-full" />
        </div>
      </template>

      <!-- Tab list -->
      <o-scroll
        ref="scrollRef"
        x-axis
        :y-axis="false"
        :options="{ visibility: 'hidden' }"
      >
        <div class="flex relative h-full items-center px-2">
          <!-- Tab item -->
          <template v-for="tab in tabs" :key="tab.path">
            <nuxt-link
              v-if="
                $gates.access(tab.path) &&
                  (owner ||
                    ($grants.access(tab.path) && $features.access(tab.path)))
              "

              :ref="tab.path"
              :to="tab.path"
              class="z-10 flex h-8 flex-shrink-0 items-center text-sm -mb-px leading-none hover:text-primary-600 focus:outline-none px-2 mr-2 md:mr-4"
              :class="[
                active && tab.path === active.path
                  ? 'text-primary-600 active:text-primary-600 font-medium dark:text-primary-400'
                  : 'opacity-80',
                $features.access(tab.path) ? '' : 'opacity-50'
              ]"
            >
              <UIcon v-if="tab.icon && tab.icon.startsWith('lucide:')" :name="tab.icon" class="size-4 lg:mr-3 p-2 md:p-0 hidden lg:block" />
              <o-icon v-else-if="tab.icon" 
                :icon="tab.icon"
                :size="16"
                class="lg:mr-3 p-2 md:p-0 hidden lg:block"
              />
              <span>{{ $t(tab.label) }}</span>
            </nuxt-link>
          </template>

          <!-- Underline -->
          <span
            class="absolute transition-all ease-in-out duration-200 h-2px bottom-0 bg-primary-600 shadow'"
            :style="underline"
          />
        </div>
      </o-scroll>

      <!-- Right nav arrow-->
      <template v-if="showArrows">
        <div class="py-2 pr-2 h-full">
          <span class="border-r dark:border-gray-800 h-full" />
        </div>
        <o-button
          flat
          size="sm"
          icon="chevronRight"
          @click="handleMoveScroll(1)"
        />
      </template>

      <slot name="trailing" />
    </div>

    <div :class="containerClass">
      <o-scroll class="py-3">
        <!-- Active section header -->
        <div
          v-if="showHeader && active"
          class="w-full flex items-center justify-between px-5 py-2"
        >
          <div>
            <h2 class="font-medium text-gray-700">
              {{ active.label }}
            </h2>
            <p v-if="active.info" class="text-sm text-gray-600 font-normal">
              {{ active.info }}
            </p>
          </div>

          <!-- Control slot -->
          <slot name="control" />
        </div>

        <slot />
      </o-scroll>
    </div>
  </div>
</template>

<script setup lang="ts">
import FontFaceObserver from 'fontfaceobserver'
import debounce from 'lodash/debounce'

const instance = getCurrentInstance()
const route = useRoute()
const router = useRouter()

interface Props {
  tabs: any[] | Record<string, any>
  navClass?: string
  containerClass?: string
  showArrows?: boolean
  showHeader?: boolean
  showTabs?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  navClass: 'border-b border-gray-200 px-5 dark:border-gray-800',
  containerClass: 'h-10 flex-grow flex-shrink',
  showArrows: true,
  showHeader: false,
  showTabs: true,
})

const scrollRef = ref<any>()

const underline = ref({
  left: '0px',
  width: '0px',
})

const activeIndex = computed(() => {
  const path = route.path
  const tabsList = props.tabs as any[]

  return tabsList.findIndex((tab: any) => tab.path === path)
})

const active = computed(() => {
  const tabsList = props.tabs as any[]
  return tabsList[activeIndex.value]
})

const owner = computed(() => {
  const employee = (instance?.proxy as any)?.$auth?.info?.employee
  return employee?.role?.isRoleTypeOwner
})

function calcUnderline () {
  const act = active.value
  const path = act ? act.path : ''
  const item = instance?.proxy?.$refs[path] as any

  let l = '0px'
  let w = '0px'

  if (item && item[0]) {
    const el = item[0].$el

    l = el.offsetLeft + 'px'
    w = el.getBoundingClientRect().width + 'px'
  }

  underline.value = { left: l, width: w }
}

function calcScroll () {
  const element = scrollRef.value
  const path = route.path
  const tab = instance?.proxy?.$refs[path] as any

  if (tab && element) {
    element.scroll(tab[0]?.$el, 500)
  }
}

const handleResize = debounce(function () {
  calcUnderline()
  calcScroll()
}, 100)

function handleMoveScroll (position = 1) {
  const tabsList = props.tabs as any[]
  const current = activeIndex.value
  const element = scrollRef.value

  const index = current + position

  if (index >= 0 && index < tabsList.length) {
    const path = tabsList[index].path
    const tab = instance?.proxy?.$refs[path] as any

    if (tab && element) {
      element.scroll(tab[0]?.$el, 500)
      router.push({ path })
    }
  }
}

watch(active, () => {
  calcUnderline()
})

watch(() => props.tabs, () => {
  nextTick(() => {
    handleResize()
  })
})

onMounted(() => {
  nextTick(() => {
    handleResize()
  })

  const font = new FontFaceObserver('Inter')

  font.load().then(() => {
    handleResize()
  })
})
</script>
